// context/MusicPlayerContext.jsx

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";

import { BACKEND } from "../utils/api";

const MusicPlayerContext = createContext(null);

export const useMusicPlayer = () => {
  const context = useContext(MusicPlayerContext);

  if (!context) {
    throw new Error(
      "useMusicPlayer must be used within MusicPlayerProvider"
    );
  }

  return context;
};

export const MusicPlayerProvider = ({ children }) => {
  const [tracks, setTracks] = useState([]);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState("none");
  const [showFull, setShowFull] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const audioRef = useRef(null);

  // ==========================================
  // GET BACKEND URL
  // ==========================================

  const getBackendUrl = useCallback(() => {
    if (BACKEND) {
      return BACKEND;
    }

    const envUrl =
      import.meta.env?.VITE_BACKEND_URL ||
      import.meta.env?.VITE_API_URL ||
      import.meta.env?.REACT_APP_BACKEND_URL ||
      import.meta.env?.REACT_APP_API_URL;

    if (envUrl) {
      return envUrl;
    }

    if (
      typeof window !== "undefined" &&
      window.__ENV__?.BACKEND_URL
    ) {
      return window.__ENV__.BACKEND_URL;
    }

    console.error("❌ NO BACKEND URL FOUND");

    return null;
  }, []);

  // ==========================================
  // GET AUTH TOKEN
  // ==========================================

  const getToken = useCallback(() => {
    return (
      localStorage.getItem("mintza_token") ||
      sessionStorage.getItem("mintza_token") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("token")
    );
  }, []);

  const getAuthHeaders = useCallback(() => {
    const token = getToken();

    if (!token) {
      return {};
    }

    return {
      Authorization: `Bearer ${token}`,
    };
  }, [getToken]);

  // ==========================================
  // BUILD MUSIC PROXY URL
  // ==========================================

  const getMusicProxyUrl = useCallback(
    (trackId) => {
      const backendUrl = getBackendUrl();

      if (!backendUrl || !trackId) {
        return null;
      }

      return `${backendUrl.replace(
        /\/+$/
      )}/api/music/audio/${encodeURIComponent(trackId)}`;
    },
    [getBackendUrl]
  );

  // ==========================================
  // AUDIO FOCUS: PAUSE ALL OTHER MEDIA
  // ==========================================

  const pauseAllOtherMedia = useCallback(() => {
    try {
      // Pause all audio/video elements on the page
      const mediaElements = document.querySelectorAll("audio, video");

      mediaElements.forEach((el) => {
        if (el === audioRef.current) return;
        if (el.closest("iframe")) return;

        if (!el.paused && el.readyState >= 2) {
          console.log("🔇 Pausing external media:", el.src || el.currentSrc);
          el.pause();
        }
      });

      // Try to pause YouTube iframes via postMessage
      try {
        const iframes = document.querySelectorAll("iframe");
        iframes.forEach((iframe) => {
          const src = iframe.src || "";

          if (src.includes("youtube.com") || src.includes("youtu.be")) {
            try {
              iframe.contentWindow.postMessage(
                '{"event":"command","func":"pauseVideo","args":""}',
                "*"
              );
              console.log("🔇 Sent pause to YouTube iframe");
            } catch (e) {
              // Cross-origin — can't control
            }
          }

          if (src.includes("spotify.com") || src.includes("open.spotify.com")) {
            try {
              iframe.contentWindow.postMessage(
                { type: "SPOTIFY_COMMAND", payload: { command: "toggle" } },
                "*"
              );
              console.log("🔇 Sent pause to Spotify iframe");
            } catch (e) {
              // Cross-origin — can't control
            }
          }
        });
      } catch (e) {
        // Ignore iframe errors
      }
    } catch (e) {
      console.error("Error pausing other media:", e);
    }
  }, []);

  // ==========================================
  // AUDIO FOCUS: CHECK IF OTHER MEDIA PLAYING
  // ==========================================

  const isOtherMediaPlaying = useCallback(() => {
    try {
      const mediaElements = document.querySelectorAll("audio, video");

      for (const el of mediaElements) {
        if (el === audioRef.current) continue;
        if (el.closest("iframe")) continue;

        if (!el.paused && el.currentTime > 0 && el.readyState >= 2) {
          return true;
        }
      }
    } catch (e) {
      // Ignore
    }

    return false;
  }, []);

  // ==========================================
  // FETCH TRACKS
  // ==========================================

  const fetchTracks = useCallback(async () => {
    const backendUrl = getBackendUrl();

    if (!backendUrl) {
      setError("Backend URL is not configured");
      setTracks([]);
      return;
    }

    const url = `${backendUrl.replace(
      /\/+$/
    )}/api/music`;

    console.log("🎵 Fetching music from:", url);

    setLoading(true);
    setError(null);

    try {
      const token = getToken();

      console.log(
        "🎵 Auth token exists:",
        !!token
      );

      const headers = {
        "Content-Type": "application/json",
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers,
      });

      console.log(
        "🎵 Music response:",
        response.status
      );

      if (response.status === 401) {
        throw new Error(
          "Authentication required - please log in"
        );
      }

      if (!response.ok) {
        const text = await response
          .text()
          .catch(() => "");

        throw new Error(
          `${response.status}: ${text.substring(
            0,
            300
          )}`
        );
      }

      const data = await response.json();

      console.log(
        "🎵 Raw music API response:",
        data
      );

      const rawTracks = Array.isArray(data)
        ? data
        : data?.tracks ||
          data?.data ||
          [];

      // ==========================================
      // NORMALIZE BACKEND TRACK DATA
      // ==========================================

      const trackList = rawTracks.map(
        (track) => {
          const proxyUrl =
            getMusicProxyUrl(track.id);

          const originalAudioUrl =
            track.audio_url ||
            track.file_url ||
            track.url ||
            null;

          return {
            ...track,

            audio_url:
              proxyUrl ||
              originalAudioUrl,

            url:
              proxyUrl ||
              originalAudioUrl,

            original_audio_url:
              originalAudioUrl,

            cover:
              track.cover_url ||
              track.cover ||
              track.thumbnail ||
              null,

            thumbnail:
              track.thumbnail ||
              track.cover_url ||
              track.cover ||
              null,

            explicit:
              track.explicit ??
              track.is_explicit ??
              false,

            createdat:
              track.createdat ||
              track.created_at ||
              null,

            created_at:
              track.created_at ||
              track.createdat ||
              null,

            duration:
              Number(track.duration) || 0,

            plays:
              Number(
                track.plays ??
                track.play_count ??
                0
              ) || 0,

            listens:
              Number(track.listens) || 0,

            likes:
              Number(track.likes) || 0,
          };
        }
      );

      console.log(
        `✅ Got ${trackList.length} music tracks`
      );

      setTracks(trackList);
      setError(null);
    } catch (err) {
      console.error(
        "❌ Fetch music error:",
        err
      );

      setError(
        err?.message ||
          "Failed to load music tracks"
      );

      setTracks([]);
    } finally {
      setLoading(false);
    }
  }, [
    getBackendUrl,
    getToken,
    getMusicProxyUrl,
  ]);

  // ==========================================
  // FETCH FAVORITES
  // ==========================================

  const fetchFavorites = useCallback(
    async () => {
      const backendUrl =
        getBackendUrl();

      const token = getToken();

      if (!backendUrl || !token) {
        return;
      }

      const url = `${backendUrl.replace(
        /\/+$/
      )}/api/music/favorites`;

      try {
        console.log(
          "❤️ Fetching favorites from:",
          url
        );

        const response = await fetch(url, {
          method: "GET",
          headers: getAuthHeaders(),
        });

        console.log(
          "❤️ Favorites response:",
          response.status
        );

        if (!response.ok) {
          return;
        }

        const data =
          await response.json();

        const favIds =
          Array.isArray(data)
            ? data
            : data?.favorites || [];

        setFavorites(favIds);

        console.log(
          `❤️ Got ${favIds.length} favorites`
        );
      } catch (err) {
        console.error(
          "❌ Fetch favorites error:",
          err?.message
        );
      }
    },
    [
      getBackendUrl,
      getToken,
      getAuthHeaders,
    ]
  );

  // ==========================================
  // INITIAL DATA LOAD
  // ==========================================

  useEffect(() => {
    fetchTracks();
  }, [fetchTracks]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  // ==========================================
  // PLAY TRACK (WITH AUTH - BLOB APPROACH)
  // ==========================================

  const handleTrackSelect = useCallback(
    async (track) => {
      if (!track) {
        console.error(
          "❌ No track supplied"
        );
        return;
      }

      const proxyUrl =
        getMusicProxyUrl(track.id);

      const fallbackUrl =
        track.audio_url ||
        track.file_url ||
        track.url;

      const src =
        proxyUrl || fallbackUrl;

      if (!src) {
        console.error(
          "❌ No audio URL:",
          track
        );

        setError(
          "This track has no audio URL"
        );

        return;
      }

      console.log(
        "▶️ Playing:",
        track.title
      );

      console.log(
        "🎵 Proxy Audio URL:",
        src
      );

      if (
        src.includes(
          "cdn.mintza.xyz"
        )
      ) {
        console.warn(
          "⚠️ OLD CDN URL DETECTED:",
          src
        );
      }

      const audio =
        audioRef.current;

      if (!audio) {
        console.error(
          "❌ Audio element does not exist"
        );

        return;
      }

      try {
        // ====================================
        // STOP CURRENT PLAYBACK
        // ====================================

        audio.pause();

        // ====================================
        // CLEANUP OLD BLOB URL
        // ====================================

        if (audio._blobUrl) {
          URL.revokeObjectURL(
            audio._blobUrl
          );
          audio._blobUrl = null;
        }

        // ====================================
        // RESET OLD SOURCE
        // ====================================

        audio.removeAttribute("src");

        audio.load();

        setCurrentTrack({
          ...track,

          audio_url: src,
          url: src,
        });

        setProgress(0);

        setDuration(
          Number(track.duration) || 0
        );

        setPlaying(false);

        setError(null);

        // ====================================
        // FETCH AUDIO WITH AUTH HEADERS
        // ====================================

        const token = getToken();

        const fetchHeaders = {};

        if (token) {
          fetchHeaders[
            "Authorization"
          ] = `Bearer ${token}`;
        }

        console.log(
          "🎵 Fetching audio with auth headers...",
          !!token
        );

        const response = await fetch(
          src,
          {
            method: "GET",
            headers: fetchHeaders,
          }
        );

        console.log(
          "🎵 Audio fetch response:",
          response.status,
          response.statusText
        );

        if (!response.ok) {
          const errorText =
            await response
              .text()
              .catch(() => "");

          throw new Error(
            `Audio fetch failed (${response.status}): ${errorText.substring(
              0,
              200
            )}`
          );
        }

        // ====================================
        // CREATE BLOB URL
        // ====================================

        const blob =
          await response.blob();

        console.log(
          "✅ Audio blob created:",
          blob.type || "unknown type",
          `${(blob.size / 1024).toFixed(
            1
          )}KB`
        );

        if (blob.size === 0) {
          throw new Error(
            "Received empty audio file from server"
          );
        }

        let mimeType = blob.type;

        if (!mimeType || mimeType === "") {
          if (src.includes(".mp3"))
            mimeType = "audio/mpeg";
          else if (src.includes(".wav"))
            mimeType = "audio/wav";
          else if (src.includes(".ogg"))
            mimeType = "audio/ogg";
          else if (src.includes(".m4a"))
            mimeType = "audio/mp4";
          else if (src.includes(".flac"))
            mimeType = "audio/flac";
          else mimeType = "audio/mpeg";
        }

        const blobUrl = URL.createObjectURL(
          new Blob([blob], { type: mimeType })
        );

        console.log(
          "✅ Blob URL created:",
          blobUrl.substring(0, 50) +
            "..."
        );

        // ====================================
        // SET SOURCE AND LOAD
        // ====================================

        audio.src = blobUrl;
        audio._blobUrl = blobUrl;
        audio.volume = volume;
        audio.preload = "auto";

        // ====================================
        // WAIT FOR METADATA
        // ====================================

        await new Promise(
          (resolve, reject) => {
            let finished = false;

            const cleanup = () => {
              audio.removeEventListener(
                "loadedmetadata",
                onLoaded
              );

              audio.removeEventListener(
                "error",
                onError
              );

              audio.removeEventListener(
                "abort",
                onAbort
              );

              audio.removeEventListener(
                "canplaythrough",
                onCanPlay
              );
            };

            const finish = (
              callback
            ) => {
              if (finished) return;

              finished = true;

              cleanup();

              callback();
            };

            const onLoaded = () => {
              console.log(
                "✅ loadedmetadata fired"
              );
              finish(resolve);
            };

            const onCanPlay = () => {
              console.log(
                "✅ canplaythrough fired"
              );
              finish(resolve);
            };

            const onError = () => {
              console.error(
                "❌ Error during audio load:",
                audio.error
              );
              finish(() =>
                reject(
                  new Error(
                    `Browser could not decode audio (code: ${audio.error?.code}, type: ${mimeType})`
                  )
                )
              );
            };

            const onAbort = () => {
              finish(() =>
                reject(
                  new Error(
                    "Audio loading was aborted"
                  )
                )
              );
            };

            audio.addEventListener(
              "loadedmetadata",
              onLoaded,
              { once: true }
            );

            audio.addEventListener(
              "canplaythrough",
              onCanPlay,
              { once: true }
            );

            audio.addEventListener(
              "error",
              onError,
              { once: true }
            );

            audio.addEventListener(
              "abort",
              onAbort,
              { once: true }
            );

            audio.load();

            setTimeout(() => {
              if (
                !finished &&
                audio.readyState >= 2
              ) {
                console.log(
                  "⚠️ Using timeout fallback - audio ready state:",
                  audio.readyState
                );
                finish(resolve);
              } else if (!finished) {
                finish(() =>
                  reject(
                    new Error(
                      "Audio loading timed out"
                    )
                  )
                );
              }
            }, 15000);
          }
        );

        console.log(
          "✅ Audio metadata loaded"
        );

        console.log(
          "🎵 Duration:",
          audio.duration
        );

        console.log(
          "🎵 Ready state:",
          audio.readyState
        );

        // ====================================
        // PAUSE OTHER MEDIA THEN PLAY
        // ====================================

        pauseAllOtherMedia();

        await audio.play();

        console.log(
          "✅ Playback started"
        );

        setPlaying(true);
      } catch (err) {
        console.error(
          "❌ Play failed:",
          err
        );

        console.error(
          "❌ Audio URL:",
          src
        );

        console.error(
          "❌ Audio error:",
          audio.error
        );

        if (audio.error) {
          console.error(
            "❌ MediaError code:",
            audio.error.code
          );

          console.error(
            "❌ MediaError message:",
            audio.error.message
          );
        }

        setPlaying(false);

        let userMessage =
          "Unable to play this audio file.";

        if (
          err.message?.includes("401") ||
          err.message?.includes("403")
        ) {
          userMessage =
            "Authentication required. Please log in again.";
        } else if (
          err.message?.includes("404")
        ) {
          userMessage =
            "Audio file not found on server.";
        } else if (
          err.message?.includes("decode")
        ) {
          userMessage =
            "Audio format not supported by your browser.";
        } else if (
          err.message?.includes("timed out")
        ) {
          userMessage =
            "Audio loading timed out. Check your connection.";
        } else if (
          err.message?.includes("empty")
        ) {
          userMessage =
            "Server returned an empty audio file.";
        } else if (
          err.message?.includes("500")
        ) {
          userMessage =
            "Server error occurred. Please try again later.";
        }

        setError(userMessage);
      }
    },
    [volume, getMusicProxyUrl, getToken, pauseAllOtherMedia]
  );

  // ==========================================
  // TOGGLE PLAY / PAUSE
  // ==========================================

  const togglePlay =
    useCallback(() => {
      if (
        !audioRef.current ||
        !currentTrack
      ) {
        return;
      }

      const audio =
        audioRef.current;

      if (playing) {
        audio.pause();

        setPlaying(false);
      } else {
        // Pause other media before resuming
        pauseAllOtherMedia();

        audio
          .play()
          .then(() => {
            setPlaying(true);
          })
          .catch((err) => {
            console.error(
              "❌ Resume playback failed:",
              err
            );

            console.error(
              "❌ Current audio source:",
              audio.src
            );

            console.error(
              "❌ Audio error:",
              audio.error
            );

            setPlaying(false);
          });
      }
    }, [
      playing,
      currentTrack,
      pauseAllOtherMedia,
    ]);

  // ==========================================
  // SEEK
  // ==========================================

  const seek = useCallback(
    (time) => {
      if (!audioRef.current) {
        return;
      }

      const newTime =
        Number(time);

      if (
        !Number.isFinite(newTime)
      ) {
        return;
      }

      if (
        Number.isFinite(
          audioRef.current.duration
        )
      ) {
        const safeTime = Math.max(
          0,
          Math.min(
            newTime,
            audioRef.current
              .duration
          )
        );

        audioRef.current.currentTime =
          safeTime;

        setProgress(safeTime);

        return;
      }

      audioRef.current.currentTime =
        newTime;

      setProgress(newTime);
    },
    []
  );

  // ==========================================
  // VOLUME
  // ==========================================

  const changeVolume =
    useCallback((vol) => {
      const newVolume =
        Math.max(
          0,
          Math.min(
            1,
            Number(vol)
          )
        );

      setVolume(newVolume);

      if (audioRef.current) {
        audioRef.current.volume =
          newVolume;
      }
    }, []);

  // ==========================================
  // NEXT TRACK
  // ==========================================

  const nextTrack =
    useCallback(() => {
      if (
        !currentTrack ||
        tracks.length === 0
      ) {
        return;
      }

      const currentIndex =
        tracks.findIndex(
          (track) =>
            track.id ===
            currentTrack.id
        );

      if (currentIndex === -1) {
        return;
      }

      let nextIndex;

      if (shuffle) {
        nextIndex =
          Math.floor(
            Math.random() *
              tracks.length
          );

        if (tracks.length > 1) {
          while (
            nextIndex ===
            currentIndex
          ) {
            nextIndex =
              Math.floor(
                Math.random() *
                  tracks.length
              );
          }
        }
      } else {
        nextIndex =
          (currentIndex + 1) %
          tracks.length;
      }

      handleTrackSelect(
        tracks[nextIndex]
      );
    }, [
      currentTrack,
      tracks,
      shuffle,
      handleTrackSelect,
    ]);

  // ==========================================
  // PREVIOUS TRACK
  // ==========================================

  const prevTrack =
    useCallback(() => {
      if (
        !currentTrack ||
        tracks.length === 0
      ) {
        return;
      }

      if (
        audioRef.current &&
        audioRef.current
          .currentTime > 3
      ) {
        audioRef.current.currentTime =
          0;

        setProgress(0);

        return;
      }

      const currentIndex =
        tracks.findIndex(
          (track) =>
            track.id ===
            currentTrack.id
        );

      if (currentIndex === -1) {
        return;
      }

      const previousIndex =
        currentIndex <= 0
          ? tracks.length - 1
          : currentIndex - 1;

      handleTrackSelect(
        tracks[previousIndex]
      );
    }, [
      currentTrack,
      tracks,
      handleTrackSelect,
    ]);

  // ==========================================
  // ADD FAVORITE
  // ==========================================

  const addToFavorites =
    useCallback(
      async (trackId) => {
        const backendUrl =
          getBackendUrl();

        const token =
          getToken();

        if (
          !backendUrl ||
          !token
        ) {
          console.error(
            "❌ Cannot add favorite: not authenticated"
          );

          return;
        }

        const url =
          `${backendUrl.replace(
            /\/+$/
          )}/api/music/favorites`;

        try {
          const response =
            await fetch(url, {
              method: "POST",

              headers: {
                ...getAuthHeaders(),

                "Content-Type":
                  "application/json",
              },

              body: JSON.stringify({
                track_id: trackId,
              }),
            });

          if (response.ok) {
            setFavorites(
              (prev) => {
                if (
                  prev.includes(
                    trackId
                  )
                ) {
                  return prev;
                }

                return [
                  ...prev,
                  trackId,
                ];
              }
            );

            console.log(
              "❤️ Added favorite:",
              trackId
            );
          } else {
            console.error(
              "❌ Add favorite failed:",
              response.status
            );
          }
        } catch (err) {
          console.error(
            "❌ Add favorite error:",
            err
          );
        }
      },
      [
        getBackendUrl,
        getToken,
        getAuthHeaders,
      ]
    );

  // ==========================================
  // REMOVE FAVORITE
  // ==========================================

  const removeFromFavorites =
    useCallback(
      async (trackId) => {
        const backendUrl =
          getBackendUrl();

        const token =
          getToken();

        if (
          !backendUrl ||
          !token
        ) {
          console.error(
            "❌ Cannot remove favorite: not authenticated"
          );

          return;
        }

        const url =
          `${backendUrl.replace(
            /\/+$/
          )}/api/music/favorites/${encodeURIComponent(
            trackId
          )}`;

        try {
          const response =
            await fetch(url, {
              method: "DELETE",

              headers:
                getAuthHeaders(),
            });

          if (response.ok) {
            setFavorites(
              (prev) =>
                prev.filter(
                  (id) =>
                    id !== trackId
                )
            );

            console.log(
              "💔 Removed favorite:",
              trackId
            );
          } else {
            console.error(
              "❌ Remove favorite failed:",
              response.status
            );
          }
        } catch (err) {
          console.error(
            "❌ Remove favorite error:",
            err
          );
        }
      },
      [
        getBackendUrl,
        getToken,
        getAuthHeaders,
      ]
    );

  // ==========================================
  // AUDIO ELEMENT
  // ==========================================

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current =
        new Audio();

      audioRef.current.volume =
        volume;

      audioRef.current.preload =
        "auto";
    }

    const audio =
      audioRef.current;

    const onTimeUpdate = () => {
      setProgress(
        audio.currentTime || 0
      );
    };

    const onLoadedMetadata = () => {
      const loadedDuration =
        Number(audio.duration);

      if (
        Number.isFinite(
          loadedDuration
        ) &&
        loadedDuration > 0
      ) {
        setDuration(
          loadedDuration
        );
      }
    };

    const onEnded = () => {
      if (repeat === "one") {
        audio.currentTime = 0;

        audio
          .play()
          .catch((err) => {
            console.error(
              "Repeat play failed:",
              err
            );
          });

        return;
      }

      if (repeat === "all") {
        nextTrack();

        return;
      }

      const currentIndex =
        tracks.findIndex(
          (track) =>
            track.id ===
            currentTrack?.id
        );

      if (
        currentIndex !== -1 &&
        currentIndex <
          tracks.length - 1
      ) {
        nextTrack();
      } else {
        setPlaying(false);

        setProgress(0);
      }
    };

    const onPlay = () => {
      setPlaying(true);
    };

    const onPause = () => {
      setPlaying(false);
    };

    const onError = (event) => {
      console.error(
        "❌ Audio error:",
        event
      );

      console.error(
        "❌ Audio source:",
        audio.src
      );

      console.error(
        "❌ MediaError:",
        audio.error
      );

      setPlaying(false);
    };

    audio.addEventListener(
      "timeupdate",
      onTimeUpdate
    );

    audio.addEventListener(
      "loadedmetadata",
      onLoadedMetadata
    );

    audio.addEventListener(
      "ended",
      onEnded
    );

    audio.addEventListener(
      "play",
      onPlay
    );

    audio.addEventListener(
      "pause",
      onPause
    );

    audio.addEventListener(
      "error",
      onError
    );

    return () => {
      audio.removeEventListener(
        "timeupdate",
        onTimeUpdate
      );

      audio.removeEventListener(
        "loadedmetadata",
        onLoadedMetadata
      );

      audio.removeEventListener(
        "ended",
        onEnded
      );

      audio.removeEventListener(
        "play",
        onPlay
      );

      audio.removeEventListener(
        "pause",
        onPause
      );

      audio.removeEventListener(
        "error",
        onError
      );
    };
  }, [
    currentTrack,
    repeat,
    tracks,
    nextTrack,
    volume,
  ]);

  // ==========================================
  // AUDIO FOCUS: MONITOR FOR COMPETING MEDIA
  // ==========================================

  useEffect(() => {
    const monitorInterval = setInterval(() => {
      if (!playing) return;
      if (!audioRef.current) return;

      if (isOtherMediaPlaying()) {
        console.log("🎵 Competing media detected — pausing our music");
        audioRef.current.pause();
        setPlaying(false);
      }
    }, 500);

    return () => clearInterval(monitorInterval);
  }, [playing, isOtherMediaPlaying]);

  // ==========================================
  // AUDIO FOCUS: PAUSE OTHERS WHEN WE PLAY
  // ==========================================

  useEffect(() => {
    if (playing) {
      pauseAllOtherMedia();
    }
  }, [playing, pauseAllOtherMedia]);

  // ==========================================
  // AUDIO FOCUS: INTERCEPT NEW MEDIA ELEMENTS
  // ==========================================

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      if (!playing) return;

      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;

          const mediaElements =
            node.tagName === "AUDIO" || node.tagName === "VIDEO"
              ? [node]
              : node.querySelectorAll
              ? Array.from(node.querySelectorAll("audio, video"))
              : [];

          mediaElements.forEach((el) => {
            if (el === audioRef.current) return;

            setTimeout(() => {
              if (!el.paused) {
                console.log("🔇 Pausing newly added media:", el.src);
                el.pause();
              }
            }, 100);
          });

          if (node.tagName === "IFRAME") {
            setTimeout(() => pauseAllOtherMedia(), 500);
          }

          const childIframes = node.querySelectorAll
            ? node.querySelectorAll("iframe")
            : [];
          if (childIframes.length > 0) {
            setTimeout(() => pauseAllOtherMedia(), 500);
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [playing, pauseAllOtherMedia]);

  // ==========================================
  // AUDIO FOCUS: CAPACITOR APP STATE
  // ==========================================

  useEffect(() => {
    let cleanup = null;

    const setupCapacitor = async () => {
      try {
        const { App } = await import("@capacitor/app");

        const listener = await App.addListener(
          "appStateChange",
          ({ isActive }) => {
            if (!isActive) {
              // App went to background — keep playing like Spotify
              console.log("📱 App backgrounded — music continues");
            } else {
              // App came to foreground — check for competing media
              if (playing && isOtherMediaPlaying()) {
                console.log(
                  "📱 App foregrounded — competing media found, pausing music"
                );
                if (audioRef.current) {
                  audioRef.current.pause();
                  setPlaying(false);
                }
              }
            }
          }
        );

        cleanup = () => listener.remove();
      } catch (e) {
        // Not in Capacitor — that's fine
      }
    };

    setupCapacitor();

    return () => {
      if (cleanup) cleanup();
    };
  }, [playing, isOtherMediaPlaying]);

  // ==========================================
  // AUDIO FOCUS: VISIBILITY CHANGE (BROWSER)
  // ==========================================

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && playing) {
        if (isOtherMediaPlaying()) {
          console.log(
            "👁️ Tab visible — competing media found, pausing music"
          );
          if (audioRef.current) {
            audioRef.current.pause();
            setPlaying(false);
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
  }, [playing, isOtherMediaPlaying]);

  // ==========================================
  // AUDIO FOCUS: WINDOW FOCUS (BROWSER)
  // ==========================================

  useEffect(() => {
    const handleFocus = () => {
      if (playing && isOtherMediaPlaying()) {
        console.log(
          "🔍 Window focused — competing media found, pausing music"
        );
        if (audioRef.current) {
          audioRef.current.pause();
          setPlaying(false);
        }
      }
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [playing, isOtherMediaPlaying]);

  // ==========================================
  // CLEANUP AUDIO WHEN PROVIDER UNMOUNTS
  // ==========================================

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();

        if (audioRef.current._blobUrl) {
          URL.revokeObjectURL(
            audioRef.current._blobUrl
          );
          audioRef.current._blobUrl =
            null;
        }

        audioRef.current.removeAttribute(
          "src"
        );

        audioRef.current.load();
      }
    };
  }, []);

  // ==========================================
  // CONTEXT VALUE
  // ==========================================

  return (
    <MusicPlayerContext.Provider
      value={{
        tracks,

        currentTrack,

        playing,

        progress,

        duration,

        seek,

        volume,

        changeVolume,

        shuffle,

        setShuffle,

        repeat,

        setRepeat,

        handleTrackSelect,

        nextTrack,

        prevTrack,

        togglePlay,

        showFull,

        setShowFull,

        favorites,

        addToFavorites,

        removeFromFavorites,

        loading,

        error,

        fetchTracks,

        pauseAllOtherMedia,

        isOtherMediaPlaying,
      }}
    >
      {children}
    </MusicPlayerContext.Provider>
  );
};
